import {
  ElectionRecordResult,
  TASK_CHOICE_MAPPING,
  TASK_CHOICE_MAPPING_REVERSE,
  TASK_KIND_MAPPING,
  TASK_KIND_MAPPING_REVERSE,
} from '@/types';
import { SearchTreeNode } from '@/types/searchTreeTypes';
import { GroupRecordResult } from '@components/GroupsFetchAutocomplete';
import { TagRecordResult } from '@components/TagsFetchAutocomplete';
import { findUpdatedRocketStatusCharacter } from '@components/OtherActionsPanelContent/shared/findUpdatedRocketStatusCharacter';
import { ApplyOtherActionsMutationVars } from '@components/OtherActionsPanelContentAdv/hooks';

/**
 * The OtherActionPanelContent component is used in various contexts, and in
 * some of them the final api needs a list of contact ids, not a more general
 * search tree. This is [somewhat
 * messy](https://australiangreens.atlassian.net/browse/LIST-666?focusedCommentId=14236)
 *
 * This extremely simple function exists to make the assumptions clear and
 * provide this explanation.
 *
 * A more robust solution would be to modify the endpoints in the backend to
 * support a more general search tree, and then this function would be
 * unnecessary, but that has its own set of problems.
 */
export function extractContactIdsFromTree(tree?: SearchTreeNode) {
  if (tree && tree.op === 'filter' && tree.filter.name === 'contactIds') {
    return tree.filter.value;
  }
  return undefined;
}

type MapStatesForApplyProps = {
  groupAddEntities: GroupRecordResult[];
  groupRemoveEntities: GroupRecordResult[];
  newGroupNames: GroupRecordResult[];
  tagAddEntities: TagRecordResult[];
  tagRemoveEntities: TagRecordResult[];
  updateRocketStatus: string | null;
  electionValue: ElectionRecordResult | null;
  taskKindType: string | null;
  levelOfInterest: string | null;
  updatePathwayStep: { label: string; id: number } | null;
  updatePathwayOption: {
    label: string;
    id: number;
  } | null;
  notes: string;
};

const strictRocketStatusCharacter = (rocketStatusFullString: string): 'P' | 'C' | 'X' => {
  const code = findUpdatedRocketStatusCharacter(rocketStatusFullString);
  if (!code) throw new Error('Invalid rocket status');
  return code;
};

export function mapStatesForApplyOtherActions({
  groupAddEntities,
  groupRemoveEntities,
  newGroupNames,
  tagAddEntities,
  tagRemoveEntities,
  updateRocketStatus,
  electionValue,
  taskKindType,
  levelOfInterest,
  updatePathwayStep,
  updatePathwayOption,
  notes,
}: MapStatesForApplyProps) {
  const mv: Omit<ApplyOtherActionsMutationVars, 'tree'> = {};

  if (groupAddEntities.length > 0)
    mv.addToGroupIds = groupAddEntities.map((x: GroupRecordResult) => x.id);

  if (groupRemoveEntities.length > 0)
    mv.removeFromGroupIds = groupRemoveEntities.map((x: GroupRecordResult) => x.id);

  if (newGroupNames.length > 0)
    mv.createGroupTitles = newGroupNames.map((x: GroupRecordResult) => x.label);

  if (tagAddEntities.length > 0) mv.addToTagIds = tagAddEntities.map((x: TagRecordResult) => x.id);

  if (tagRemoveEntities.length > 0)
    mv.removeFromTagIds = tagRemoveEntities.map((x: TagRecordResult) => x.id);

  if (updateRocketStatus)
    mv.updateSupporterStatus = strictRocketStatusCharacter(updateRocketStatus);

  if (electionValue && taskKindType && levelOfInterest) {
    mv.updateTasks = {
      electionId: electionValue.id,
      taskKindType: TASK_KIND_MAPPING_REVERSE[
        taskKindType
      ] as unknown as keyof typeof TASK_KIND_MAPPING,
      levelOfInterest: TASK_CHOICE_MAPPING_REVERSE[
        levelOfInterest
      ] as unknown as keyof typeof TASK_CHOICE_MAPPING,
      notes,
    };
  }

  if (updatePathwayStep && updatePathwayOption) {
    mv.updatePathway = {
      stepId: updatePathwayStep.id,
      optionId: updatePathwayOption.id as 1 | 2 | 3,
    };
  }

  return mv;
}
